import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { AppConstant } from '../app.constants';
import { FormArray, FormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SharedService } from '../shared.service';
import { CurrencyUtilityService } from './currency-utility';

@Injectable({
  providedIn: 'root'
})
export class CommonUtilityService {
  private searchFilterOpened = new BehaviorSubject(true);
  searchFilterOpened$ = this.searchFilterOpened.asObservable();
  private sideMenuOpened = new BehaviorSubject(true);
  sideMenuOpened$ = this.sideMenuOpened.asObservable();
  private _sharedService!: SharedService

  dynamicValueRadiusUnit = '(km)'

  get sharedService(): SharedService {
    if (!this._sharedService) {
      this._sharedService = this.injector.get(SharedService);
    }
    return this._sharedService;
  }

  changesearchFilterOpened(data: boolean) {
    this.searchFilterOpened.next(data)
  }

  changesideMenuOpened(data: boolean) {
    this.sideMenuOpened.next(data)
  }

  constructor(private _snackBar: MatSnackBar, private injector : Injector,
    private currencyService : CurrencyUtilityService) { }

  isNullValue(data: any) {
    if (data == null || data == undefined) {
      return "";
    }
    return data;
  }

  getErrorString(str) {
    return str.replace("error:", "");
  }

  public handleErrorText(error: HttpErrorResponse, defaultMessage: string) {
    switch (error.status) {
      case 412:
        let errorData = error.error.data.errors;
        let message = '';
        for (const key in errorData) {
          errorData[key].forEach((x) => message = `${message} \n` + x);
        }
        return message;
      case 500:
        return error.error ? this.getErrorString(error.error) : error.statusText;
      case 400:
        return error.error ? error.error : error.statusText;
      case 401:
        return `You are unauthorized`;
      default:
        return defaultMessage;
    }
  }

  public setBlankControlsToNull(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);

      if (!control) return;

      // 🔹 Special case for isBeneficiarySame
      if (key === 'isBeneficiarySame') {
        if (control.value === null || control.value === '' || control.value === undefined) {
          control.setValue(false);
        }
        return; // no further processing needed
      }

      // 🔹 General blank → null logic
      if (control.value === '' || control.value === undefined) {
        control.setValue(null);
      }

      // 🔹 Recursive handling
      if (control instanceof FormGroup) {
        this.setBlankControlsToNull(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach(ctrl => {
          if (ctrl instanceof FormGroup) {
            this.setBlankControlsToNull(ctrl);
          }
        });
      }
    });
  }

  isGreateInsuredRiskFactorValue(insurable_interest: any, limitStr, page = '') {
    for (const item of insurable_interest) {
      const valueStr = page === '' ? item.insuredRiskFactorValue : item.reinsuranceExposureValue;

      if (limitStr && valueStr && limitStr !== '') {
        const limit = parseInt(limitStr?.replace(/,/g, ''));
        const value = parseInt(valueStr.replace(/,/g, ''));

        if (limit && value && limit > value) {
          const message = page === ''
            ? `Insurance Limits cannot exceed the exposure value, otherwise this may not be insurance. Please reduce the Limit before proceeding.`
            : `Reinsurance Limits cannot exceed the Reinsurance exposure value, otherwise this may not be Reinsurance. Please reduce the Limit before proceeding.`;
          this.showSnackbar(message);
          return false;
        }
      }
    }
    return true;
  }

  showSnackbar(msg: string, type?: any) {
    if (!type) {
      this._snackBar.open(msg, 'Okay', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
    } else {
      this._snackBar.open(msg, 'Okay', {
        duration: 5000
      });
    }
  }

  getStructureType(req: any, subType: string, adapterName : string , pricingParameters: any, i): string {
    let content = '';
    let parsedParams: Record<string, any> = {};

    pricingParameters =
    req?.insurable_interest?.[i]?.pricingParameters ??
    req?.ReinsurableInterestCalculation?.pricingParameters ??
    pricingParameters;

    // Parse pricing parameters if it's a string
    if (typeof pricingParameters === 'string') {
      try {
        parsedParams = JSON.parse(pricingParameters);
      } catch (err) {
        console.error('Invalid pricingParameters JSON:', err);
      }
    } else if (typeof pricingParameters === 'object' && pricingParameters !== null) {
      parsedParams = pricingParameters;
    }

    const adapterLower = adapterName?.toLowerCase() || '';
    // Build content from conditionalInputs config
    for (const config of AppConstant.conditionalInputs) {
      const match = config.substrings.every(sub => adapterLower.includes(sub.toLowerCase()));

      if (match && parsedParams.hasOwnProperty(config.key)) {
        const label = config.tooltip || config.name;
        let value = parsedParams[config.key];

        if (config.type === 'selection' && config.values) {
          // If the value exists in the map, replace with display label
          value = this.sharedService.addCommas(config.values[value]) ?? this.sharedService.addCommas(value);
        }
        if (label.toLowerCase().includes('radius')) {
          content += `${label}: ${value} ${this.dynamicValueRadiusUnit}<br>`;
        } else {
          content += `${label}: ${value} <br>`;
        }
      }
    }

    // Add subtype info if available
    if (req?.EventSubType?.subType) {
      const subtypeLine = `Event Subtype: ${req.EventSubType.subType}`;
      content = `${subtypeLine}<br><br>${content}`;
    }
    const country = req?.['insurable_interest']?.[i]?.InsurableInterest?.insurableInterestLocationCountry?.toLowerCase?.() ||
      req?.['reinsurable_interest']?.ReinsurableInterest?.reinsuranceCountry?.toLowerCase?.();
    if (['australia', 'au'].includes(country)) {
      if (/hurricane/i.test(content)) {
        content = content.replace(/hurricane/gi, 'Tropical Cyclone');
      }
    }

    return content || '';
  }

  getPaymentEligibilityForDoc(req: any, subType: string, pricingParameters: string): string {
    let content = req?.EventSubType?.paymentEligibility ? `${req.EventSubType.paymentEligibility}` : '';

    if(content){
      return content
    }

    return '';
  }

  checkClientCondition(data) {
    const contractType = data.contractType;
    const treatyOrFacultative = data.treatyOrFacultative;
    return contractType == 'Insurance' || (contractType !== 'Insurance' && treatyOrFacultative == 'facultative');
  }

  roundToTwo(val : any){
    return parseFloat(val.toFixed(2))
  }


  checkValidFieldsBeforeFirmQuote(request: any) {
    const islands = [
      "Baker Island",
      "Howland Island",
      "Jarvis Island",
      "Johnston Atoll",
      "Kingman Reef",
      "Midway Atoll",
      "Navassa Island",
      "Palmyra Atoll",
      "United States Minor Outlying Islands",
      "Wake Island"
    ];

    let excludedKeys = ['BrokerPolicy', 'brokerPolicyId', 'surplusLinesTransactionNumber', 'surplusLinesBrokerLicenceNumber', 'parentRequestId', 'parentRequest', 'parentRequestId', "historicLossesEstimateDate", "historicLossesEstimateValue","premiumType",
      "historicLossesEstimateCurrency", "historicLossesEstimateDate_2", "historicLossesEstimateValue_2", "historicLossesEstimateCurrency_2", "historicLossesEstimateDate_3", "historicLossesEstimateValue_3", "historicLossesEstimateCurrency_3",
      "premiumPeriodIndicator","beneficiaryFirstName","beneficiaryFamilyName","beneficiaryCompanyName","beneficiaryLocationZipOrPostalCode","reinsuredLocationZipOrPostalCode", "nbi_pdf", "beneficiaryLocationAddressLine2"];


    if (request.contractType && request.contractType == 'Insurance') {
      let array = ['reinsuredName', 'reinsuredLocationStreetNo', 'reinsuredLocationCity', 'reinsuredLocationCountrySubDivision', 'reinsuredLocationCountry', 'reinsuredLocationZipOrPostalCode', 'yokahuDistribution',
        'ReinsurableInterest', 'ReinsurableInterestCalculation', 'insurerClientId', 'reinsurableInterestId', 'DistributorInsurer', 'reIICalcId', 'reinsurableInterestId','treatyOrFacultative'];
      excludedKeys = [...excludedKeys, ...array];
    } else {
      if (request.treatyOrFacultative) {
        if (request.treatyOrFacultative == 'treaty') {
          let array = ['clientDetailsId', 'reinsuredName', 'reinsuredLocationStreetNo', 'reinsuredLocationCity', 'reinsuredLocationCountrySubDivision', 'reinsuredLocationCountry', 'reinsuredLocationZipOrPostalCode',
            'yokahuDistribution', 'IICalcId', 'insurable_interest', 'insurableInterest', 'ClientDetail'];
          excludedKeys = [...excludedKeys, ...array];
        } else {
          let array = ['insurerClientId', 'reinsurableInterestId', 'ReinsurableInterest', 'ReinsurableInterestCalculation', 'DistributorInsurer', 'reIICalcId', 'reinsurableInterestId']
          excludedKeys = [...excludedKeys, ...array];
          if (request.reinsuredLocationCountry && !AppConstant.DISTRICT_COUNTRIES.includes(request.reinsuredLocationCountry.toLowerCase())) {
            excludedKeys.push('reinsuredLocationCountryDistrict');
          }
        }
      }
    }
    for (const key in request) {
      if (!excludedKeys.includes(key)) {
        if (request[key] === '' || request[key] === null || request[key] === undefined) {
          console.log(`missing field: ${key}`)
          return false;
        }
      }
    }
    return true;
  }

  normalizeArray(data: any): any[] {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return [data]; // wrap single value into array
  }

  getPayoutAmountForHistoricEvents(
    event: any,
    values: any,
    eventType: any,
    IILimit: any,
    currency = '',
    request: any,
    locationCurrency: any,
    rate = null,
    page: any
  ) {
    const USDlimit = this.convertToUSD(IILimit, rate, locationCurrency);
    const limit =
      locationCurrency === currency
        ? IILimit
        : this.convertFromUSD(USDlimit, request.exchangeRate, currency);

    const currencySymbol = this.currencyService.setCurrencySymbol(currency);

    // Normalize thresholds
    const thresholds = this.normalizeArray(values?.thresholds).map(range => {
      const [lowerStr, upperStr] = (range + '').split(' - ');

      return {
        lower: parseFloat(lowerStr),
        upper: upperStr?.toLowerCase() === 'inf'
          ? Infinity
          : parseFloat(upperStr)
      };
    });

    // Normalize payouts
    const payouts = this.normalizeArray(values?.payouts).map(x =>
      page === 'price' ? this.sharedService.getFloatPayout(x) : x
    );

    if (!thresholds.length || !payouts.length) {
      return '0 ' + currencySymbol;
    }

    let value = 0;

    switch (eventType?.toLowerCase()) {

      case 'hurricane': {
        value = parseFloat(event.wind_speed?.split(' ')[0] || '0');
        break;
      }

      case 'flood': {
        value = parseFloat(event.rainFall?.split(' ')[0] || '0');
        break;
      }

      case 'earthquake': {
        value = parseFloat(event.magnitude || '0');
        break;
      }

      default:
        return '0 ' + currencySymbol;
    }

    // Find matching threshold
    const index = thresholds.findIndex(
      ({ lower, upper }) => lower <= value && value < upper
    );

    if (index === -1) {
      return '0 ' + currencySymbol;
    }

    // If upper bound is Infinity → 100% payout
    const payout =
      thresholds[index].upper === Infinity
        ? 1
        : payouts[index];

    return this.sharedService.addCommas(
      this.roundToTwo(limit * payout)
    ) + ' ' + currencySymbol;
  }

  convertToUSD(amount, rate, currency) {
    if (amount == null || amount === '') return 0;

    const cleanAmount = parseFloat(amount.toString().replace(/,/g, ''));
    if (isNaN(cleanAmount) || !rate) return 0;

    if (currency && currency.toLowerCase() === 'usd') {
      return this.roundToTwo(cleanAmount);
    }

    return this.roundToTwo(cleanAmount / rate);
  }

  convertFromUSD(amountUSD, rate, currency) {
    if (amountUSD == null || amountUSD === '') return 0;

    const cleanAmount = parseFloat(amountUSD.toString().replace(/,/g, ''));
    if (isNaN(cleanAmount) || !rate) return 0;

    // If converting to USD, just round
    if (currency && currency.toLowerCase() === 'usd') {
      return this.roundToTwo(cleanAmount);
    }

    return this.roundToTwo(cleanAmount * rate);
  }
}  
